import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DashboardColumnSort } from '@epicuro-next/features/dashboard';
import { Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import {
  IListBasketInsightQueryParams,
  IListBasketInsightResponse,
  IListReportResponse,
  IListViewersResponse,
  ITopArticleFiltersResponse,
  ITopArticleResponse,
  ITopListsFiltersResponse,
  ITopListsResponse,
  TopSellingProductsRequest,
  TopSellingProductsResponse,
} from '../models';
import { IListLiveViewersResponse } from '../models/live-viewers';
import { ReferenceType } from '../models/reference-type';
import { REPORTING_API_TOKEN } from '../token';

@Injectable()
export class ReportingApiService {
  constructor(
    private http: HttpClient,
    @Inject(REPORTING_API_TOKEN) private apiUrl: string,
  ) {}

  public getProductListReports(
    ids: string[],
    currencyCode: string | null,
    dateRange?: { from: string; to: string },
  ) {
    return this.http
      .post<IListReportResponse>(`${this.apiUrl}/productlist/byfilter`, {
        referenceType: ReferenceType.LIST,
        references: ids,
        currencyCode,
        fromDate: dateRange?.from,
        toDate: dateRange?.to,
      })
      .pipe(take(1));
  }

  public getTopListRevenues(
    topN: number,
    dateRangeUTC: {
      from: string;
      to: string;
    },
    brandOrgIds: string[] = [],
  ) {
    return this.http
      .get<ITopListsResponse>(`${this.apiUrl}/listrevenue/top`, {
        params: {
          TopN: topN.toString(),
          FromDate: dateRangeUTC.from,
          ToDate: dateRangeUTC.to,
          BrandOrgIds: brandOrgIds,
        },
      })
      .pipe(
        take(1),
        map((resp) => resp.topLists),
      );
  }

  public getTopListFilters() {
    return this.http.get<ITopListsFiltersResponse>(
      `${this.apiUrl}/listrevenue/filters`,
    );
  }

  public getProductListBasketInsight(
    queryParams: IListBasketInsightQueryParams,
  ) {
    const params = new HttpParams()
      .set('listId', queryParams.listId)
      .set('topN', `${queryParams.topN}`)
      .set('sortBy', `${queryParams.sortBy}`);
    return this.http
      .get<IListBasketInsightResponse>(`${this.apiUrl}/listrevenue/baskets`, {
        params,
      })
      .pipe(take(1));
  }

  public getProductListLiveViewers(listId: string) {
    return this.http
      .get<IListLiveViewersResponse>(
        `${this.apiUrl}//productlistsession/${listId}`,
      )
      .pipe(map((resp) => resp.sessions));
  }

  public getTopSellingProducts(payload: TopSellingProductsRequest) {
    const params = new HttpParams()
      .set('listId', payload.listId)
      .set('topN', `${payload.size}`);
    return this.http
      .get<TopSellingProductsResponse>(`${this.apiUrl}/listrevenue/products/`, {
        params,
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  public getProductListViewers(listId: string, numberOfTopViewers: number = 7) {
    return this.http
      .get<IListViewersResponse>(
        `${this.apiUrl}/productlist/${listId}/views?topN=${numberOfTopViewers}`,
      )
      .pipe(catchError((err) => throwError(() => err)));
  }

  public getTopArticles(
    topN: number,
    dateRangeUTC: {
      from: string;
      to: string;
    },
    brandOrgIds: string[] = [],
    categories: string[] = [],
    orderType: string = '',
    sort?: DashboardColumnSort,
  ): Observable<ITopArticleResponse> {
    return this.http.get<ITopArticleResponse>(
      `${this.apiUrl}/topproducts/top`,
      {
        params: {
          TopN: topN.toString(),
          FromDate: dateRangeUTC.from,
          ToDate: dateRangeUTC.to,
          BrandOrgIds: brandOrgIds,
          OrderType: orderType,
          Categories: categories,
          SortField: sort ? sort.field : '',
          SortDirection: sort ? sort.direction.toString() : '',
        },
      },
    );
  }

  public getTopArticleFilters(): Observable<ITopArticleFiltersResponse> {
    return this.http.get<ITopArticleFiltersResponse>(
      `${this.apiUrl}/topproducts/filters`,
    );
  }
}
